import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSignalRListener } from './useSignalR';
import { queryClient } from '../config/queryClient';
import { SIGNALR_LISTENERS } from '../config/signalr';
import { useWorkflowChrome } from '../components/Layout/WorkflowChromeContext';
import { debug } from '../services/logger';
import { useMissedEventBeat } from './useMissedEventBeat';
import { GET_FILE_EXTRACT } from '../consts/queryKeys';

// 250ms coalescing window. SignalR can emit several status events in close
// succession when a single backend transition flips multiple fields (e.g.
// extraction completes → output_table starts → period record touched).
// Without coalescing each event would invalidate the period query and trigger
// a refetch. 250ms is small enough to feel instantaneous while comfortably
// swallowing event bursts.
const COALESCE_MS = 250;

interface WorkflowStatusPayload {
  environment?: string;
  workflow_id?: string;
  period_id?: string;
  [key: string]: unknown;
}

export type PeriodDataFamily =
  'period' | 'extraction' | 'transformation' | 'files' | 'jurisdictions';

// What a missed-event beat or an unannounced mutation refreshes: everything but jurisdictions, which only a workflow-period event moves.
const STATUS_FAMILIES: PeriodDataFamily[] = ['period', 'extraction', 'transformation', 'files'];

const keyMatches = (key: unknown[], periodId: string, families: Set<PeriodDataFamily>): boolean => {
  const [prefix] = key;
  if (families.has('period') && prefix === 'GET_PERIOD_BY_ID') return key[2] === periodId;
  if (families.has('extraction') && prefix === 'GET_EXTRACTION_STATS') return key[1] === periodId;
  if (families.has('transformation') && prefix === 'GET_TRANSFORMATION_STATS')
    return key[1] === periodId;
  if (families.has('jurisdictions') && prefix === 'GET_JURISDICTIONS') return key[1] === periodId;
  // Keyed by file and sheet, not period; only the extraction page reads these, for what it has open.
  if (families.has('extraction') && prefix === 'useGetAllStatusExtractions') return true;
  if (families.has('extraction') && prefix === GET_FILE_EXTRACT) return true;
  if (families.has('extraction') || families.has('files')) {
    // Sheet-category rows are what a period could extract; saved extractions are what it holds.
    if (prefix === 'GET_SHEET_CATEGORIES_FOR_PERIOD') return key[1] === periodId;
    if (prefix === 'GET_WORKFLOW_EXTRACT')
      return (key[1] as { period_id?: string } | undefined)?.period_id === periodId;
  }
  return false;
};

/**
 * Refresh what the stepper reads for one period. Exported because SignalR is not the only thing
 * that changes it: a mutation the backend does not announce (deleting an extraction) calls this too.
 *
 * @returns how many queries matched — useful in dev to confirm the call landed.
 */
export const invalidatePeriodStatusQueries = (
  periodId?: string,
  families: Iterable<PeriodDataFamily> = STATUS_FAMILIES
): number => {
  if (!periodId) return 0;
  const wanted = new Set(families);
  let matched = 0;
  queryClient.invalidateQueries({
    predicate: query => {
      const key = query.queryKey;
      if (!Array.isArray(key) || !keyMatches(key, periodId, wanted)) return false;
      matched++;
      return true;
    }
  });
  return matched;
};

const EVENT_FAMILIES: Array<[string, PeriodDataFamily[]]> = [
  [SIGNALR_LISTENERS.ON_EXTRACTION_STATUS_UPDATED, ['period', 'extraction']],
  [SIGNALR_LISTENERS.ON_AUTOMATION_EXTRACTION_STATUS_UPDATED, ['period', 'extraction']],
  [SIGNALR_LISTENERS.ON_FUND_EXTRACTION_UPDATED, ['period', 'extraction']],
  [SIGNALR_LISTENERS.ON_TRANSFORMATION_STATUS_UPDATED, ['period', 'transformation']],
  [SIGNALR_LISTENERS.ON_AUTOMATION_TRANSFORMATION_STATUS_UPDATED, ['period', 'transformation']],
  [SIGNALR_LISTENERS.ON_TRANSFORMATION_UPDATED, ['period', 'transformation']],
  [SIGNALR_LISTENERS.ON_FILE_PROCESSING_UPDATED, ['period', 'files']],
  [SIGNALR_LISTENERS.ON_OUTPUT_TABLE_STATUS_UPDATED, ['period']],
  [SIGNALR_LISTENERS.ON_AUTOMATION_REPORT_STATUS_UPDATED, ['period']],
  [SIGNALR_LISTENERS.ON_APPROVAL_STATUS_UPDATED, ['period']]
];

/** Every event that moves a period, for code that refreshes one outside the workflow chrome. */
export const PERIOD_EVENTS: string[] = [
  ...EVENT_FAMILIES.map(([event]) => event),
  SIGNALR_LISTENERS.ON_WORKFLOW_PERIOD_UPDATED
];

/**
 * Listens to every workflow-progress SignalR event and refreshes the period
 * query so the stepper updates in near-real-time. Replaces the periodic
 * polling that previously lived inside `useWorkflowStages`.
 *
 * Designed to be mounted once at the chrome level (inside
 * `WorkflowChromeProvider`) — every page that consumes the stepper benefits
 * without each page wiring its own listener.
 *
 * Invalidates **every** `GET_PERIOD_BY_ID` variant for the current period
 * (including the FCA/ESMA/FormPF Report containers' `customKey` variants)
 * via a predicate-form `invalidateQueries`. `GET_WORKFLOW` is also
 * invalidated on `WorkflowPeriodUpdate` so the AppTopBar period chips
 * refresh when the workflow's periods array changes.
 */
export const useWorkflowChromeStatusSync = (): void => {
  const ctx = useWorkflowChrome();
  const workflowId = ctx.workflowId;
  const periodId = ctx.periodId;

  // Stable refs so the per-event handlers can be defined once outside React's
  // render cycle while still seeing the latest ids on each event.
  const workflowIdRef = useRef<string | undefined>(workflowId);
  const periodIdRef = useRef<string | undefined>(periodId);
  useEffect(() => {
    workflowIdRef.current = workflowId;
    periodIdRef.current = periodId;
  });

  // Coalesced invalidate: the families of every event in the window, refreshed in one pass.
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingFamiliesRef = useRef<Set<PeriodDataFamily>>(new Set());
  const flushInvalidations = useCallback(
    (families: Iterable<PeriodDataFamily> = STATUS_FAMILIES) => {
      const currentPeriodId = periodIdRef.current;
      if (!currentPeriodId) return;

      const matched = invalidatePeriodStatusQueries(currentPeriodId, families);
      debug('WorkflowChromeStatusSync.invalidate', { matched, periodId: currentPeriodId });
    },
    []
  );

  const scheduleInvalidate = useCallback(
    (families: PeriodDataFamily[]) => {
      families.forEach(family => pendingFamiliesRef.current.add(family));
      if (pendingTimerRef.current != null) return;
      pendingTimerRef.current = setTimeout(() => {
        pendingTimerRef.current = null;
        const pending = pendingFamiliesRef.current;
        pendingFamiliesRef.current = new Set();
        flushInvalidations(pending);
      }, COALESCE_MS);
    },
    [flushInvalidations]
  );

  useEffect(() => {
    return () => {
      if (pendingTimerRef.current != null) {
        clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
    };
  }, []);

  // Permissive matcher — mirrors the Quarters dashboard's
  // `matchPeriodInThisWorkflow` so both surfaces behave identically.
  // Rule: DROP only when we have strong evidence of mismatch (an ID field
  // is present AND clearly points elsewhere). Missing IDs count as "could
  // be for us" and pass through, so batch backend emissions that omit
  // `period_id` still trigger a refresh — this was the source of the
  // stepper's laggy / stuck-status behaviour.
  const matchesCurrentContext = useCallback((payload?: WorkflowStatusPayload): boolean => {
    const currentPeriodId = periodIdRef.current;
    const currentWorkflowId = workflowIdRef.current;
    if (!currentPeriodId) return false;
    if (payload?.workflow_id && payload.workflow_id !== currentWorkflowId) return false;
    if (payload?.period_id && payload.period_id !== currentPeriodId) return false;
    return true;
  }, []);

  const handlers = useMemo(() => {
    const byEvent: Record<string, (payload?: WorkflowStatusPayload) => void> = {};
    for (const [event, families] of EVENT_FAMILIES) {
      byEvent[event] = payload => {
        debug('WorkflowChromeStatusSync.signalr', {
          event,
          payload_period_id: payload?.period_id,
          payload_workflow_id: payload?.workflow_id,
          current_period_id: periodIdRef.current,
          current_workflow_id: workflowIdRef.current
        });
        if (matchesCurrentContext(payload)) scheduleInvalidate(families);
      };
    }
    return byEvent;
  }, [matchesCurrentContext, scheduleInvalidate]);

  const handleWorkflowPeriodEvent = useCallback(
    (payload?: WorkflowStatusPayload) => {
      const currentPeriodId = periodIdRef.current;
      const currentWorkflowId = workflowIdRef.current;
      if (!currentPeriodId) return;

      const periodMatches = !!payload?.period_id && payload.period_id === currentPeriodId;
      const workflowMatches =
        !payload?.period_id && !!payload?.workflow_id && payload.workflow_id === currentWorkflowId;
      if (!periodMatches && !workflowMatches) return;

      // Period chips in the AppTopBar carry workflow-level status — refresh
      // the workflow record alongside the period.
      queryClient.invalidateQueries(['GET_WORKFLOW']);
      // The only event a jurisdiction change sends, and the one that can move anything else.
      scheduleInvalidate([...STATUS_FAMILIES, 'jurisdictions']);
    },
    [scheduleInvalidate]
  );

  const L = SIGNALR_LISTENERS;
  useSignalRListener(L.ON_EXTRACTION_STATUS_UPDATED, handlers[L.ON_EXTRACTION_STATUS_UPDATED]);
  useSignalRListener(
    L.ON_AUTOMATION_EXTRACTION_STATUS_UPDATED,
    handlers[L.ON_AUTOMATION_EXTRACTION_STATUS_UPDATED]
  );
  useSignalRListener(L.ON_FUND_EXTRACTION_UPDATED, handlers[L.ON_FUND_EXTRACTION_UPDATED]);
  useSignalRListener(
    L.ON_TRANSFORMATION_STATUS_UPDATED,
    handlers[L.ON_TRANSFORMATION_STATUS_UPDATED]
  );
  useSignalRListener(
    L.ON_AUTOMATION_TRANSFORMATION_STATUS_UPDATED,
    handlers[L.ON_AUTOMATION_TRANSFORMATION_STATUS_UPDATED]
  );
  useSignalRListener(L.ON_TRANSFORMATION_UPDATED, handlers[L.ON_TRANSFORMATION_UPDATED]);
  useSignalRListener(L.ON_FILE_PROCESSING_UPDATED, handlers[L.ON_FILE_PROCESSING_UPDATED]);
  useSignalRListener(L.ON_OUTPUT_TABLE_STATUS_UPDATED, handlers[L.ON_OUTPUT_TABLE_STATUS_UPDATED]);
  useSignalRListener(
    L.ON_AUTOMATION_REPORT_STATUS_UPDATED,
    handlers[L.ON_AUTOMATION_REPORT_STATUS_UPDATED]
  );
  useSignalRListener(L.ON_APPROVAL_STATUS_UPDATED, handlers[L.ON_APPROVAL_STATUS_UPDATED]);
  useSignalRListener(L.ON_WORKFLOW_PERIOD_UPDATED, handleWorkflowPeriodEvent);

  /* And the same refresh on a slow clock, for the eleven messages above when one of them is lost.
   *
   * Not a return to the polling this hook replaced: that ran regardless and carried the stepper on
   * its own. This is thirty seconds, paused while the tab is hidden, and it is here because a
   * dropped event does not make the stepper late — it makes it wrong until somebody reloads. The
   * run's band sits directly above the stepper reading a period it polls itself, so the two
   * disagreed in public: one saying a stage was starting, the other calling it finished. */
  const beat = useCallback(() => flushInvalidations(), [flushInvalidations]);
  useMissedEventBeat(beat, !!periodId);
};
